import React from "react";
import Modal from "../common/modal/Modal";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import styles from "./ProductDetailModal.module.scss";

interface ProductDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  productVariant: any;
  orderPrice?: number;
  orderCurrency?: string;
  orderQuantity?: number;
}

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  isOpen,
  onClose,
  productVariant,
  orderPrice,
  orderCurrency,
  orderQuantity,
}) => {
  const { t } = useTranslation("translation");

  if (!productVariant) return null;

  const getLocalizedName = (lang: string): string => {
    const priorities: Record<string, string[]> = {
      az: ["name_az", "name_en", "name_de", "name_es"],
      en: ["name_en", "name_az", "name_de", "name_es"],
      es: ["name_es", "name_en", "name_az", "name_de"],
      de: ["name_de", "name_en", "name_az", "name_es"],
    };

    const order = priorities[lang] || priorities["en"];

    for (const key of order) {
      const value = productVariant[key];
      if (value && typeof value === "string" && value.trim()) {
        return value;
      }
    }

    return "Unnamed Variant";
  };

  const getLocalizedDescription = (lang: string): string => {
    const priorities: Record<string, string[]> = {
      az: [
        "description_az",
        "description_en",
        "description_de",
        "description_es",
      ],
      en: [
        "description_en",
        "description_az",
        "description_de",
        "description_es",
      ],
      es: [
        "description_es",
        "description_en",
        "description_az",
        "description_de",
      ],
      de: [
        "description_de",
        "description_en",
        "description_az",
        "description_es",
      ],
    };

    const order = priorities[lang] || priorities["en"];

    for (const key of order) {
      const value = productVariant[key];
      if (value && typeof value === "string" && value.trim()) {
        return value;
      }
    }

    return "";
  };

  const getLocalizedAttributeName = (attribute: any, lang: string): string => {
    const priorities: Record<string, string[]> = {
      az: [
        "attribute_name_az",
        "attribute_name_en",
        "attribute_name_de",
        "attribute_name_es",
        "attribute_name",
      ],
      en: [
        "attribute_name_en",
        "attribute_name_az",
        "attribute_name_de",
        "attribute_name_es",
        "attribute_name",
      ],
      es: [
        "attribute_name_es",
        "attribute_name_en",
        "attribute_name_az",
        "attribute_name_de",
        "attribute_name",
      ],
      de: [
        "attribute_name_de",
        "attribute_name_en",
        "attribute_name_az",
        "attribute_name_es",
        "attribute_name",
      ],
    };

    const order = priorities[lang] || priorities["en"];

    for (const key of order) {
      const value = attribute?.[key];
      if (value && typeof value === "string" && value.trim()) {
        return value;
      }
    }

    return "-";
  };

  const getLocalizedAttributeValue = (attribute: any, lang: string): string => {
    const priorities: Record<string, string[]> = {
      az: ["value_az", "value_en", "value_de", "value_es", "value"],
      en: ["value_en", "value_az", "value_de", "value_es", "value"],
      es: ["value_es", "value_en", "value_az", "value_de", "value"],
      de: ["value_de", "value_en", "value_az", "value_es", "value"],
    };

    const order = priorities[lang] || priorities["en"];

    for (const key of order) {
      const value = attribute?.[key];
      if (value && typeof value === "string" && value.trim()) {
        return value;
      }
    }

    return "-";
  };

  const productName = getLocalizedName(i18n.language);
  const productDescription = getLocalizedDescription(i18n.language);
  const images = productVariant?.images || [];
  const attributes = productVariant?.attributes || [];
  const product = productVariant?.product || {};

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={productName} footer={false}>
      <div className={styles.productDetail}>
        {/* Product Images */}
        {images.length > 0 && (
          <div className={styles.imagesSection}>
            <h3 className={styles.sectionTitle}>
              {t("products_details.images") || "Images"}
            </h3>
            <div className={styles.imagesGrid}>
              {images.map((img: any, idx: number) => (
                <div key={idx} className={styles.imageWrapper}>
                  <img
                    src={img?.image}
                    alt={`${productName} - ${idx + 1}`}
                    className={styles.image}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Product Information */}
        <div className={styles.infoSection}>
          <h3 className={styles.sectionTitle}>
            {t("products_details.product_info") || "Product Information"}
          </h3>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.label}>{t("labels.sku") || "SKU"}:</span>
              <span className={styles.value}>{productVariant?.sku || "-"}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.label}>
                {t("labels.stock_quantity") || "Stock Quantity"}:
              </span>
              <span className={styles.value}>
                {productVariant?.stock_quantity || 0}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.label}>
                {t("labels.price") || "Price"}:
              </span>
              <span className={styles.value}>
                {productVariant?.price?.toFixed(2) || "0.00"}{" "}
                {product?.currency?.name || ""}
              </span>
            </div>
            {productVariant?.discount_price && (
              <div className={styles.infoItem}>
                <span className={styles.label}>
                  {t("labels.discounted_price") || "Discount Price"}:
                </span>
                <span className={styles.value}>
                  {productVariant?.discount_price?.toFixed(2) || "0.00"}{" "}
                  {product?.currency?.name || ""}
                </span>
              </div>
            )}
            {productVariant?.discount_percent > 0 && (
              <div className={styles.infoItem}>
                <span className={styles.label}>
                  {t("labels.discount_percent") || "Discount"}:
                </span>
                <span className={styles.value}>
                  {productVariant?.discount_percent}%
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Order Information */}
        {(orderPrice || orderQuantity) && (
          <div className={styles.infoSection}>
            <h3 className={styles.sectionTitle}>
              {t("orders") || "Order Information"}
            </h3>
            <div className={styles.infoGrid}>
              {orderPrice && (
                <div className={styles.infoItem}>
                  <span className={styles.label}>
                    {t("columns.price") || "Order Price"}:
                  </span>
                  <span className={styles.value}>
                    {orderPrice?.toFixed(2) || "0.00"} {orderCurrency || ""}
                  </span>
                </div>
              )}
              {orderQuantity && (
                <div className={styles.infoItem}>
                  <span className={styles.label}>
                    {t("columns.quantity") || "Quantity"}:
                  </span>
                  <span className={styles.value}>{orderQuantity}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Product Description */}
        {productDescription && (
          <div className={styles.descriptionSection}>
            <h3 className={styles.sectionTitle}>
              {t("labels.description") || "Description"}
            </h3>
            <p className={styles.description}>{productDescription}</p>
          </div>
        )}

        {/* Attributes */}
        {attributes.length > 0 && (
          <div className={styles.attributesSection}>
            <h3 className={styles.sectionTitle}>
              {t("products_details.attributes") || "Attributes"}
            </h3>
            <div className={styles.attributesList}>
              {attributes.map((attr: any, idx: number) => (
                <div key={idx} className={styles.attributeItem}>
                  <span className={styles.attributeName}>
                    {getLocalizedAttributeName(attr, i18n.language)}:
                  </span>
                  <span className={styles.attributeValue}>
                    {getLocalizedAttributeValue(attr, i18n.language)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ProductDetailModal;
